/**
 * Core-100 owner direction 12 — a Technician + Service Advisor on the front-office schedule.
 *
 * The authority is the database guard on `shop_profile.staff_schedules`
 * (`guard_staff_schedules_front_office`, migration 20270206001900); this module mirrors it so the
 * editor can explain a refusal before the save, and gives CORE-BN one rule for when such a person
 * counts as front office.
 *
 * - The opt-in is explicit: an entry with `multi_role: true`, offered only to someone who works as a
 *   Service Advisor (the SERVICE_ADVISOR work role) and whose legacy role is not already front office.
 * - No double-counting: on every working day the front-office window may not overlap the person's
 *   technician shift (per-day `hours` map, else the flat shift columns — the planners' precedence).
 * - They count as front office ONLY inside that window; outside it their hours are technician time.
 */

export interface FrontOfficeScheduleEntry {
  user_id: string;
  role?: string;
  shift_start: string;
  shift_end: string;
  working_days: number[];
  /** The explicit opt-in for a person whose job is otherwise in the bays. */
  multi_role?: boolean;
}

export interface TechnicianHoursLike {
  hours?: Record<string, { open?: string | null; close?: string | null } | null> | null;
  shift_start?: string | null;
  shift_end?: string | null;
}

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
export const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const FRONT_OFFICE_LEGACY = new Set(['OWNER', 'MANAGER', 'ORG_ADMIN', 'SERVICE_WRITER']);

function toMinutes(t: string | null | undefined): number | null {
  if (!t) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(String(t));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** May this person be offered the "also front office" option? */
export function mayOptIntoFrontOffice(legacyRole: string | null | undefined, workRoles: readonly string[]): boolean {
  return !FRONT_OFFICE_LEGACY.has(String(legacyRole ?? '').toUpperCase()) && workRoles.includes('SERVICE_ADVISOR');
}

/** The technician's bay window on a weekday (0 = Sunday), or null when they do not work the bays that day. */
export function technicianDayWindow(tech: TechnicianHoursLike, dow: number): { open: number; close: number } | null {
  const map = tech.hours && typeof tech.hours === 'object' ? tech.hours : null;
  const hasMap = !!map && Object.keys(map).length > 0;
  let open: number | null;
  let close: number | null;
  if (!hasMap) {
    open = toMinutes(tech.shift_start);
    close = toMinutes(tech.shift_end);
  } else {
    const byIndex = map![String(dow)];
    const byName = Object.entries(map!).find(([k]) => k.toLowerCase() === DAY_NAMES[dow])?.[1];
    const entry = byIndex ?? byName ?? null;
    open = toMinutes(entry?.open ?? null);
    close = toMinutes(entry?.close ?? null);
  }
  return open !== null && close !== null && close > open ? { open, close } : null;
}

/** The weekdays on which a front-office window would overlap the bay shift (empty = no double-counting). */
export function overlappingDays(entry: Pick<FrontOfficeScheduleEntry, 'shift_start' | 'shift_end' | 'working_days'>,
  tech: TechnicianHoursLike | null): number[] {
  if (!tech) return [];
  const start = toMinutes(entry.shift_start);
  const end = toMinutes(entry.shift_end);
  if (start === null || end === null || end <= start) return [];
  return [...new Set(entry.working_days ?? [])].filter((d) => {
    const w = technicianDayWindow(tech, d);
    return !!w && start < w.close && w.open < end;
  }).sort((a, b) => a - b);
}

/** Is a multi-role person on front-office duty at this shop-local weekday + minute? */
export function onFrontOfficeDutyAt(entry: FrontOfficeScheduleEntry, dow: number, minuteOfDay: number): boolean {
  if (entry.multi_role !== true) return false;
  const start = toMinutes(entry.shift_start);
  const end = toMinutes(entry.shift_end);
  return start !== null && end !== null && (entry.working_days ?? []).includes(dow)
    && minuteOfDay >= start && minuteOfDay < end;
}

const FRONT_OFFICE_LEGACY_ROLES = ['OWNER', 'MANAGER', 'ORG_ADMIN', 'SERVICE_WRITER'];

/** Shop-local weekday (0 = Sunday) and minute of day for an instant. */
export function shopLocalDayMinute(atIso: string, timeZone: string | null | undefined): { dow: number; minute: number } | null {
  const at = new Date(atIso);
  if (Number.isNaN(at.getTime())) return null;
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: timeZone || 'UTC', weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
      .formatToParts(at);
    const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
    const dow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday'));
    const minute = Number(get('hour')) * 60 + Number(get('minute'));
    return dow < 0 || !Number.isFinite(minute) ? null : { dow, minute };
  } catch {
    return null;
  }
}

/**
 * Front-office staff for an instant, with no double-counting (CORE-BN).
 *
 * People whose legacy role is front office count as before. Anyone else who acts as front office
 * (a Technician + Service Advisor) counts ONLY while an explicit front-office schedule entry covers
 * the instant; the rest of their day is technician capacity. With no shop-local time, or no entry,
 * they do not count — never both.
 */
export function frontOfficeRowsAt<T extends { user_id: string; legacy_role?: string }>(
  rows: readonly T[],
  schedules: readonly FrontOfficeScheduleEntry[] | null | undefined,
  atIso: string,
  timeZone: string | null | undefined,
): T[] {
  const local = shopLocalDayMinute(atIso, timeZone);
  return rows.filter((r) => {
    if (r.legacy_role === undefined || FRONT_OFFICE_LEGACY_ROLES.includes(String(r.legacy_role).toUpperCase())) return true;
    const entry = (schedules ?? []).find((s) => s.user_id === r.user_id);
    return !!entry && !!local && onFrontOfficeDutyAt(entry, local.dow, local.minute);
  });
}
